/**
 * MLBScheduleWidget — MLB schedule using MLB Stats API as primary source
 * Also merges TheOddsAPI data when available (for odds availability indicator)
 *
 * Fetches today + 3 days from statsapi.mlb.com
 * Groups games by date with day labels (Today, Tomorrow, Wed, etc.)
 */

import { useState, useEffect, useCallback } from 'react'
import { getTeamLogo } from '../lib/logoMaps'
import { fetchFromProxy, fetchOddsEvents as fetchOddsProxyEvents, imageUrl } from '../lib/api'
import './MLBScheduleWidget.css'

// ---------------------------------------------------------------------------
// MLB Stats API team abbreviation → our canonical codes
// The API returns full team names; we need codes for logo lookup
// ---------------------------------------------------------------------------
const MLB_TEAM_ABBREV = {
  'Arizona Diamondbacks': 'ARI',
  'Atlanta Braves': 'ATL',
  'Baltimore Orioles': 'BAL',
  'Boston Red Sox': 'BOS',
  'Chicago Cubs': 'CHC',
  'Chicago White Sox': 'CHW',
  'Cincinnati Reds': 'CIN',
  'Cleveland Guardians': 'CLE',
  'Colorado Rockies': 'COL',
  'Detroit Tigers': 'DET',
  'Houston Astros': 'HOU',
  'Kansas City Royals': 'KCR',
  'Los Angeles Angels': 'LAA',
  'Los Angeles Dodgers': 'LAD',
  'Miami Marlins': 'MIA',
  'Milwaukee Brewers': 'MIL',
  'Minnesota Twins': 'MIN',
  'New York Mets': 'NYM',
  'New York Yankees': 'NYY',
  'Oakland Athletics': 'OAK',
  'Philadelphia Phillies': 'PHI',
  'Pittsburgh Pirates': 'PIT',
  'San Diego Padres': 'SDP',
  'Seattle Mariners': 'SEA',
  'San Francisco Giants': 'SFG',
  'St. Louis Cardinals': 'STL',
  'Tampa Bay Rays': 'TBR',
  'Texas Rangers': 'TEX',
  'Toronto Blue Jays': 'TOR',
  'Washington Nationals': 'WSH',
  // Spring training / alternate names
  'LA Angels': 'LAA',
  'LA Dodgers': 'LAD',
  'NY Mets': 'NYM',
  'NY Yankees': 'NYY',
  'SD Padres': 'SDP',
  'SF Giants': 'SFG',
  'TB Rays': 'TBR',
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function formatDateYMD(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getDayLabel(dateStr) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(dateStr + 'T00:00:00')
  target.setHours(0, 0, 0, 0)
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24))

  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  return target.toLocaleDateString('en-US', { weekday: 'long' })
}

function formatDateDisplay(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatGameTime(isoStr) {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

function getTeamCode(teamName) {
  if (!teamName) return null
  return MLB_TEAM_ABBREV[teamName] || null
}

function parseScheduleGraphicDate(imagePath, timestamp) {
  if (imagePath) {
    const match = String(imagePath).match(/(\d{4}-\d{2}-\d{2})(?=\.png$)/)
    if (match) return match[1]
  }
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  return formatDateYMD(date)
}

function formatRelativeUpdateTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

function formatGraphicStamp(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// ---------------------------------------------------------------------------
// Fetch MLB Stats API for a single date
// ---------------------------------------------------------------------------
async function fetchMLBDate(dateStr) {
  const url = `https://statsapi.mlb.com/api/v1/schedule?sportId=1&date=${dateStr}&hydrate=linescore`
  const res = await fetch(url, { signal: AbortSignal.timeout(10000) })
  if (!res.ok) throw new Error(`MLB API ${res.status}`)
  const data = await res.json()

  const games = []
  for (const dateEntry of (data.dates || [])) {
    for (const g of (dateEntry.games || [])) {
      const away = g.teams?.away
      const home = g.teams?.home
      const linescore = g.linescore

      games.push({
        gamePk: g.gamePk,
        date: dateEntry.date,
        away: away?.team?.name || 'TBD',
        home: home?.team?.name || 'TBD',
        awayScore: away?.score ?? null,
        homeScore: home?.score ?? null,
        status: g.status?.detailedState || g.status?.abstractGameState || 'Scheduled',
        statusCode: g.status?.statusCode || 'S',
        startTime: g.gameDate,
        inning: linescore?.currentInning || null,
        inningHalf: linescore?.inningHalf || null,
        seriesDescription: g.seriesDescription || '',
      })
    }
  }
  return games
}

// ---------------------------------------------------------------------------
// Fetch TheOddsAPI events (optional enrichment)
// ---------------------------------------------------------------------------
async function fetchOddsEvents() {
  try {
    const result = await fetchOddsProxyEvents('baseball_mlb')
    if (result.status !== 'ok') return []
    return Array.isArray(result.data) ? result.data : []
  } catch {
    return []
  }
}

async function fetchScheduleImages() {
  try {
    const result = await fetchFromProxy('/images', { source: 'schedule', limit: '8' })
    if (result.status !== 'ok') return []
    return Array.isArray(result.data?.images) ? result.data.images : []
  } catch {
    return []
  }
}

async function fetchScheduleUpdates() {
  try {
    const result = await fetchFromProxy('/posts', { channel: '#mlb-schedule', limit: '12' })
    if (result.status !== 'ok') return []
    const posts = Array.isArray(result.data?.posts) ? result.data.posts : []
    return posts.filter(post => post.post_type !== 'graphic')
  } catch {
    return []
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function MLBScheduleWidget() {
  const [gamesByDate, setGamesByDate] = useState({})
  const [oddsEventIds, setOddsEventIds] = useState(new Set())
  const [scheduleGraphic, setScheduleGraphic] = useState(null)
  const [scheduleUpdates, setScheduleUpdates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Build date range: today + 3 days
      const dates = []
      for (let i = 0; i < 4; i++) {
        const d = new Date()
        d.setDate(d.getDate() + i)
        dates.push(formatDateYMD(d))
      }

      // Fetch all dates in parallel + odds events + Discord-backed graphics/updates
      const [oddsEvents, scheduleImages, recentScheduleUpdates, ...dateResults] = await Promise.all([
        fetchOddsEvents(),
        fetchScheduleImages(),
        fetchScheduleUpdates(),
        ...dates.map(d => fetchMLBDate(d).catch(() => [])),
      ])

      // Build odds set for "has odds" badge
      const oddsSet = new Set()
      for (const ev of oddsEvents) {
        // Match by team names loosely
        if (ev.home_team) oddsSet.add(ev.home_team.toLowerCase())
        if (ev.away_team) oddsSet.add(ev.away_team.toLowerCase())
      }
      setOddsEventIds(oddsSet)

      // Group games by date
      const grouped = {}
      dates.forEach((dateStr, idx) => {
        const games = dateResults[idx] || []
        if (games.length > 0) {
          grouped[dateStr] = games
        }
      })

      const todayKey = dates[0]
      const datedImages = scheduleImages.map(img => ({
        ...img,
        dateKey: parseScheduleGraphicDate(img.path, img.timestamp),
      }))
      const selectedGraphic =
        datedImages.find(img => img.dateKey === todayKey) ||
        datedImages.find(img => img.dateKey && dates.includes(img.dateKey)) ||
        datedImages[0] ||
        null

      setScheduleGraphic(selectedGraphic ? {
        ...selectedGraphic,
        src: imageUrl(selectedGraphic.path),
      } : null)
      setScheduleUpdates(recentScheduleUpdates.slice(0, 5))

      setGamesByDate(grouped)
      setLastFetch(new Date())
    } catch (err) {
      console.error('MLBScheduleWidget fetch error:', err)
      setError(err.message || 'Failed to load schedule')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 60000) // refresh every 60s
    return () => clearInterval(interval)
  }, [loadData])

  // Check if a game has odds available
  const hasOdds = (game) => {
    const homeLower = game.home?.toLowerCase()
    const awayLower = game.away?.toLowerCase()
    return oddsEventIds.has(homeLower) || oddsEventIds.has(awayLower)
  }

  // Total games across all dates
  const totalGames = Object.values(gamesByDate).reduce((sum, games) => sum + games.length, 0)
  const liveGames = Object.values(gamesByDate).flat().filter(g =>
    ['I', 'IR', 'MA', 'MI'].includes(g.statusCode) || g.status === 'In Progress'
  ).length
  const dateKeys = Object.keys(gamesByDate).sort()

  // Determine status class
  const getStatusClass = (game) => {
    const sc = game.statusCode
    if (['F', 'FT', 'FR', 'FO', 'CR', 'GO'].includes(sc)) return 'msw-status-final'
    if (['I', 'IR', 'MA', 'MI'].includes(sc)) return 'msw-status-live'
    if (['D', 'DI', 'DR'].includes(sc)) return 'msw-status-postponed'
    return 'msw-status-scheduled'
  }

  const getCardClass = (game) => {
    const sc = game.statusCode
    if (['I', 'IR', 'MA', 'MI'].includes(sc)) return 'msw-live'
    if (['F', 'FT', 'FR', 'FO'].includes(sc)) return 'msw-final'
    return ''
  }

  const getStatusLabel = (game) => {
    const sc = game.statusCode
    if (['F', 'FT', 'FR', 'FO'].includes(sc)) return 'Final'
    if (['I', 'IR', 'MA', 'MI'].includes(sc)) return 'Live'
    if (['D', 'DI', 'DR'].includes(sc)) return 'PPD'
    if (['S', 'P', 'PW'].includes(sc)) return 'Scheduled'
    return game.status
  }

  const renderTeamRow = (teamName, score, isAway, opponentScore) => {
    const code = getTeamCode(teamName)
    const logoPath = code ? getTeamLogo(code, 'mlb') : null
    const isFinal = score !== null && opponentScore !== null
    const isWinner = isFinal && score > opponentScore

    return (
      <div className={`msw-team-row ${isAway ? 'msw-away' : ''} ${isWinner ? 'msw-winner' : ''}`}>
        {logoPath ? (
          <img src={logoPath} alt={teamName} className="msw-team-logo" loading="lazy" />
        ) : (
          <div className="msw-team-logo-placeholder">{teamName?.charAt(0) || '?'}</div>
        )}
        <span className="msw-team-name">{teamName}</span>
        {score !== null && (
          <span className="msw-team-score">{score}</span>
        )}
      </div>
    )
  }

  return (
    <div className="msw-widget">
      <button className="msw-refresh-btn" onClick={loadData} title="Refresh schedule">
        &#x21bb; Refresh
      </button>

      <div className="msw-header">
        <h2>
          <span className="msw-header-icon">&#x26BE;</span>
          MLB Schedule
        </h2>
        <p className="msw-subtitle">
          MLB Stats API
          <span className="msw-source-badge">Live</span>
        </p>
      </div>

      {scheduleGraphic && (
        <div className="msw-hero">
          <div className="msw-hero-card">
            <div className="msw-hero-header">
              <div>
                <div className="msw-hero-label">Schedule Graphic</div>
                <div className="msw-hero-date">
                  {scheduleGraphic.dateKey ? formatDateDisplay(scheduleGraphic.dateKey) : 'Latest available'}
                </div>
              </div>
              <div className="msw-hero-stamp">
                {formatGraphicStamp(scheduleGraphic.timestamp)}
              </div>
            </div>
            <img
              src={scheduleGraphic.src}
              alt="MLB schedule graphic"
              className="msw-hero-image"
              loading="lazy"
            />
          </div>
        </div>
      )}

      {scheduleUpdates.length > 0 && (
        <div className="msw-updates">
          <div className="msw-updates-header">
            <span className="msw-updates-label">Recent Schedule Updates</span>
            <span className="msw-updates-count">{scheduleUpdates.length}</span>
          </div>
          <div className="msw-updates-list">
            {scheduleUpdates.map((post, idx) => (
              <div key={`${post.timestamp || idx}-${idx}`} className="msw-update-item">
                <div className="msw-update-meta">
                  <span className="msw-update-title">{post.title || 'Schedule update'}</span>
                  <span className="msw-update-time">{formatRelativeUpdateTime(post.timestamp)}</span>
                </div>
                {post.description && (
                  <div className="msw-update-desc">{post.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="msw-error">
          <span>&#x26A0; {error}</span>
          <button onClick={loadData}>Retry</button>
        </div>
      )}

      {loading && !totalGames && (
        <div className="msw-loading">
          <div className="msw-spinner" />
          <span>Loading MLB schedule...</span>
        </div>
      )}

      {!loading && !error && totalGames === 0 && (
        <div className="msw-empty">
          <div className="msw-empty-icon">&#x26BE;</div>
          <div>No games scheduled for the next 4 days</div>
        </div>
      )}

      {dateKeys.map(dateStr => {
        const games = gamesByDate[dateStr]
        const dayLabel = getDayLabel(dateStr)
        const isToday = dayLabel === 'Today'

        return (
          <div key={dateStr} className={`msw-day-section ${isToday ? 'msw-day-today' : ''}`}>
            <div className="msw-day-header">
              <span className="msw-day-label">{dayLabel}</span>
              <span className="msw-day-date">{formatDateDisplay(dateStr)}</span>
              <span className="msw-day-count">
                {games.length} game{games.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="msw-games-grid">
              {games.map(game => (
                <div key={game.gamePk} className={`msw-game-card ${getCardClass(game)}`}>
                  <div className="msw-teams">
                    {renderTeamRow(game.away, game.awayScore, true, game.homeScore)}
                    <div className="msw-at-separator">@</div>
                    {renderTeamRow(game.home, game.homeScore, false, game.awayScore)}
                  </div>

                  <div className="msw-game-info">
                    {game.statusCode === 'S' || game.statusCode === 'P' || game.statusCode === 'PW' ? (
                      <span className="msw-game-time">{formatGameTime(game.startTime)}</span>
                    ) : null}
                    <span className={`msw-game-status ${getStatusClass(game)}`}>
                      {getStatusLabel(game)}
                    </span>
                    {game.inning && (
                      <span className="msw-inning">
                        {game.inningHalf === 'Top' ? 'Top' : 'Bot'} {game.inning}
                      </span>
                    )}
                    {hasOdds(game) && (
                      <span className="msw-odds-badge">Odds</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      {totalGames > 0 && (
        <div className="msw-footer">
          <div className="msw-stat">
            <span className="msw-stat-value">{totalGames}</span>
            <span className="msw-stat-label">Total Games</span>
          </div>
          <div className="msw-stat">
            <span className="msw-stat-value">{liveGames}</span>
            <span className="msw-stat-label">Live</span>
          </div>
          <div className="msw-stat">
            <span className="msw-stat-value">{dateKeys.length}</span>
            <span className="msw-stat-label">Days</span>
          </div>
          <span className="msw-powered">
            {lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : ''}
          </span>
        </div>
      )}
    </div>
  )
}
